import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, EMPTY, Observable, of, switchMap } from 'rxjs';
import type { Catalog } from '../models/catalog.model';
import { CatalogApiPort } from '../ports/catalog-api.port';
import { DiscoveryApiPort } from '@htx/discovery';
import { NotificationService } from '@htx/core';
import { TranslocoService } from '@jsverse/transloco';

@Injectable({
  providedIn: 'root'
})
export class CatalogService {
  readonly catalogApi = inject(CatalogApiPort);
  readonly discoveryApiService = inject(DiscoveryApiPort);
  readonly translocoService = inject(TranslocoService);
  readonly notificationService = inject(NotificationService);

  readonly catalogSubject: BehaviorSubject<Catalog> = new BehaviorSubject<Catalog>({ id: '', datasets: [] });
  readonly catalog$: Observable<Catalog> = this.catalogSubject.asObservable();
  readonly loadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  readonly organizationNameSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  readonly hasSearchedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly hasSearched$: Observable<boolean> = this.hasSearchedSubject.asObservable();
  readonly bpnlSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  readonly protocolUrlSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  searchByBpnl(bpnl: string): void {
    this.loadingSubject.next(true);
    this.organizationNameSubject.next(null);
    this.catalogSubject.next({ id: '', datasets: [] });
    this.hasSearchedSubject.next(true);
    this.bpnlSubject.next(bpnl);
    this.protocolUrlSubject.next(null);

    this.discoveryApiService
      .getOrganizationByBpnl(bpnl)
      .pipe(catchError(() => of(null)))
      .subscribe((response) => {
        this.organizationNameSubject.next(response?.organizationName ?? null);
      });

    this.discoveryApiService
      .getConnectorByBpnl(bpnl)
      .pipe(
        catchError((error: { status: number }) => {
          if (error.status === 404) {
            this.notificationService.showError(
              this.translocoService.translate('catalog.discovery.bpnlNotFound'),
              5000
            );
          } else {
            this.notificationService.showError(
              this.translocoService.translate('catalog.searchCatalog.errorMessage'),
              5000
            );
          }
          this.loadingSubject.next(false);
          return EMPTY;
        }),
        switchMap((discovery) => {
          this.protocolUrlSubject.next(discovery.protocolUrl);
          if (!discovery.protocolUrl) {
            this.notificationService.showError(
              this.translocoService.translate('catalog.discovery.noConnector'),
              5000
            );
            this.loadingSubject.next(false);
            return EMPTY;
          }
          return this.catalogApi.getCatalogByConnectorAddress(
            discovery.protocolUrl,
            bpnl
          ).pipe(
            catchError(() => {
              this.notificationService.showError(
                this.translocoService.translate('catalog.searchCatalog.errorMessage'),
                5000
              );
              this.loadingSubject.next(false);
              return EMPTY;
            })
          );
        })
      )
      .subscribe((catalog) => {
        this.loadingSubject.next(false);
        this.catalogSubject.next(catalog);
      });
  }

  getCatalog$(): Observable<Catalog> {
    return this.catalog$;
  }

  getOrganizationName$(): Observable<string | null> {
    return this.organizationNameSubject.asObservable();
  }

  getBpnl$(): Observable<string | null> {
    return this.bpnlSubject.asObservable();
  }

  getProtocolUrl$(): Observable<string | null> {
    return this.protocolUrlSubject.asObservable();
  }
}
